import { Button } from '@storeblocks/button';
import { type FC } from 'react';
import { generatePath } from 'react-router';
import styled from 'styled-components';

import { useOrganizationCmId } from '@/api/userSettings/hooks/useOrganizationCmId';
import { Column, Row } from '@/components/Grid';
import { InternalLink } from '@/components/InternalLink';
import { MainContent } from '@/components/MainContent';
import { ConnectSurface } from '@/components/Pretzel/ConnectSurface';
import { WithGap } from '@/components/WithGap';
import { useTexts } from '@/hooks/useTexts';

import { CenteredPageContent } from './components/CenteredPageContent';
import { PortfolioGroupCheckboxes } from './PortfolioGroupCheckboxes';
import { usePresentationContext } from './PresentationContext';
import { ScreenCheckboxes } from './ScreenCheckboxes';

export const PresentationMenuPage: FC = () => {
  const texts = useTexts();
  const organizationCmId = useOrganizationCmId();

  const { selectedScreens, globalFilter } = usePresentationContext();

  const firstScreenPath =
    selectedScreens[0] &&
    generatePath(selectedScreens[0].path!, { organizationCmId });

  const noPortfoliosSelected = globalFilter.portfolioGroups.length === 0;

  return (
    <CenteredPageContent data-test="presentation-menu">
      <MainContent>
        <WithGap gap="8">
          <h1>{texts.pages.presentation.title}</h1>
          <p className="font-body">
            {texts.pages.presentation.setup.selectScreens.description}
          </p>
        </WithGap>
        <Row>
          <Column $span={3}>
            <ScreenCheckboxes />
          </Column>

          <Column $span={3}>
            <PortfolioGroupCheckboxes />
          </Column>

          <Column $span={1}>
            {/* Empty to have space between the link list and the tips column. */}
          </Column>

          <Column $span={5}>
            <div>
              <StyledSurface>
                <WithGap gap="8">
                  <h2>{texts.pages.presentation.setup.info.title}</h2>
                  <p className="font-body">
                    {texts.pages.presentation.setup.info.description}
                  </p>
                </WithGap>
              </StyledSurface>
              <StyledSurface>
                <WithGap gap="8">
                  <h2>{texts.pages.presentation.setup.tip.title}</h2>
                  <p className="font-body">
                    {texts.pages.presentation.setup.tip.description1}{' '}
                    <Key>N</Key>{' '}
                    {texts.pages.presentation.setup.tip.description2}{' '}
                    <Key>M</Key>{' '}
                    {texts.pages.presentation.setup.tip.description3}
                  </p>
                </WithGap>
              </StyledSurface>
            </div>
          </Column>
        </Row>
        <ButtonContainer>
          <Button
            as={InternalLink}
            to={`/${organizationCmId}`}
            iconLeft="arrow_left"
            variant="tertiary"
          >
            {texts.pages.presentation.backToConnect}
          </Button>
          <WithGap direction="row">
            <Button
              as={InternalLink}
              to={firstScreenPath}
              disabled={!firstScreenPath || noPortfoliosSelected}
              data-test="start-presentation-button"
            >
              {texts.pages.presentation.startPresentation}
            </Button>
            <Button
              as={InternalLink}
              to={`/${organizationCmId}/reports/presentation/print`}
              variant="tertiary"
              iconRight="arrow_right"
              disabled={selectedScreens.length === 0 || noPortfoliosSelected}
              data-test="go-to-print-button"
            >
              {texts.pages.presentation.createReport}
            </Button>
          </WithGap>
        </ButtonContainer>
      </MainContent>
    </CenteredPageContent>
  );
};

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

const StyledSurface = styled(ConnectSurface)`
  line-height: 24px;
  margin-bottom: 24px;
`;

const Key = styled.kbd`
  border: 1px solid var(--p-color-stroke-subtle-0);
  border-radius: 4px;
  padding: 2px 8px;
  margin: 0 2px;
  box-shadow: var(--elevation-overlay);
  background-color: white;
  line-height: normal;
`;
